// src/components/CategoryManagement.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  orderBy,
  updateDoc // <-- Importa updateDoc
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI ---
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  // --- Aggiungi componenti Dialog ---
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
  // --- Fine Aggiunta ---
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import EditIcon from '@mui/icons-material/Edit'; // <-- Importa EditIcon

// --- Aggiungi il Modale di Modifica ---
const EditCategoryModal = ({ open, onClose, category, onSave, t }) => {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setName(category.name);
    }
  }, [category]);

  const handleSave = async () => {
    if (!name || !category) return;
    setIsSaving(true);
    try {
      await onSave(category.id, name);
      onClose();
    } catch (error) {
      console.error("Error updating category:", error);
      alert(t('edit_error')); // Aggiungi 'edit_error' al tuo file JSON di traduzione
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('edit_title')}</DialogTitle> {/* Aggiungi 'edit_title' */}
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('name_label')}
          type="text"
          fullWidth
          variant="standard"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('cancel_button')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : t('save_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- Fine Modale ---


function CategoryManagement({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('category');
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canManage = userRole === 'admin' || userRole === 'Engineer' || userRole === 'Storekeeper';

  // --- Aggiungi stati per il modale ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  // --- Fine Aggiunta ---


  // useEffect (invariato)
  useEffect(() => {
    if (!teamId) return;
    setIsLoading(true);
    
    const categoriesQuery = query(
      collection(db, "inventoryCategories"),
      where("teamId", "==", teamId),
      orderBy("name", "asc")
    );

    const unsubscribe = onSnapshot(categoriesQuery, (querySnapshot) => {
      const categoriesList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(categoriesList);
      setIsLoading(false);
    }, (error) => {
      console.error("Error loading categories (index needed?):", error.message);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [teamId]);

  // --- MODIFICA: Funzioni Handler (con t() per alert) ---
  const handleAddCategory = async (e) => {
    e.preventDefault();
    const categoryName = newCategoryName.trim();
    if (!categoryName) return;

    if (categories.some(cat => cat.name.toLowerCase() === categoryName.toLowerCase())) {
      alert(t('exists_error')); // Tradotto
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "inventoryCategories"), {
        teamId: teamId,
        name: categoryName
      });
      setNewCategoryName('');
    } catch (error) {
      console.error("Error adding category:", error);
      alert(t('add_error')); // Tradotto
    }
    setIsSubmitting(false);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm(t('delete_confirm'))) { // Tradotto
      try {
        await deleteDoc(doc(db, "inventoryCategories", categoryId));
      } catch (error) {
        console.error("Error deleting category:", error);
        alert(t('delete_error')); // Tradotto
      }
    }
  };
  // --- FINE MODIFICA ---

  // --- Aggiungi funzioni per modale ---
  const handleOpenEditModal = (category) => {
    setCurrentCategory(category);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setCurrentCategory(null);
    setIsEditModalOpen(false);
  };

  const handleUpdateCategory = async (categoryId, newName) => {
    if (categories.some(cat => cat.name.toLowerCase() === newName.toLowerCase() && cat.id !== categoryId)) {
      alert(t('exists_error'));
      throw new Error("Category name already exists");
    }
    const categoryRef = doc(db, "inventoryCategories", categoryId);
    await updateDoc(categoryRef, {
      name: newName
    });
  };
  // --- Fine Aggiunta ---

  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} {/* Tradotto */}
        </Typography>
      </Paper>

      {/* 2. Paper Form e Lista */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        {/* === INIZIO BLOCCO RIPRISTINATO === */}
        {canManage && (
          <Box component="form" onSubmit={handleAddCategory} sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'center' }}>
              <TextField
                label={t('name_label')}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                required
                disabled={isSubmitting}
                sx={{ flexGrow: 1, width: '100%' }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !newCategoryName}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                size="large"
                sx={{ height: '56px', width: { xs: '100%', md: 'auto' } }}
              >
                {t('add_button')} {/* Tradotto */}
              </Button>
            </Box>
          </Box>
        )}
        {/* === FINE BLOCCO RIPRISTINATO === */}
        
        {canManage && <Divider sx={{ my: 2 }} />}

        {/* Existing Categories Section */}
        <Box>
          <Typography variant="h5" gutterBottom>{t('saved_title')}</Typography>
          {isLoading && <CircularProgress size={24} />}
          {!isLoading && categories.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('empty')} {/* Tradotto */}
            </Typography>
          )}
          {!isLoading && categories.length > 0 && (
            <List dense>
              {categories.map((cat) => (
                <ListItem
                  key={cat.id}
                  divider
                  sx={{ 
                    backgroundColor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1
                  }}
                  secondaryAction={
                    canManage ? (
                      // --- Aggiungi Box per icone ---
                      <Box>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditModal(cat)} sx={{ mr: 0.5 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCategory(cat.id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Box>
                      // --- Fine Box ---
                    ) : null
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CategoryIcon />
                  </ListItemIcon>
                  <ListItemText primary={cat.name} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* --- Aggiungi il modale al render --- */}
      <EditCategoryModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        category={currentCategory}
        onSave={handleUpdateCategory}
        t={t}
      />
    </>
  );
}

export default CategoryManagement;